import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  Chip,
  Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RedeemIcon from '@mui/icons-material/Redeem';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';
import Layout from '../../components/layout/Layout';
import { customerService } from '../../services/customerService';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';

function TabPanel({ children, value, index, ...rest }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...rest}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CustomerLoyaltyReportPage = () => {
  const [tab, setTab] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await customerService.getAllCustomers({ page: 0, size: 500, sort: 'name', direction: 'ASC' });
      const list = res?.content ?? (Array.isArray(res) ? res : []);
      setCustomers(Array.isArray(list) ? list : []);
    } catch (e) {
      setCustomers([]);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchReport = useCallback(async () => {
    if (!customerId) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`/reports/customer-loyalty?customerId=${customerId}`);
      setReport(res.data);
    } catch (e) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) fetchReport();
    else setReport(null);
  }, [customerId, fetchReport]);

  const handleExportToExcel = () => {
    if (!report) return;

    try {
      setIsExporting(true);
      const selectedCustomer = customers.find(c => c.id === customerId);
      const customerName = selectedCustomer ? selectedCustomer.name : 'Unknown';

      let exportData = [];
      let fileName = '';
      let sheetName = '';
      let title = '';

      if (tab === 0) {
        // Earnings history
        exportData = (report.purchaseHistory || []).map((row, index) => ({
          'S.No': index + 1,
          'Bill #': row.orderNumber || '–',
          'Date': row.orderDate ? format(new Date(row.orderDate), 'dd/MM/yyyy') : '–',
          'Total Amount': row.total || 0,
          'Points Earned': row.pointsEarned ?? 0
        }));
        fileName = `loyalty_earnings_${customerName.replace(/\s+/g, '_').toLowerCase()}`;
        sheetName = 'Points Earned';
        title = `Customer Loyalty - Points Earned History (${customerName})`;
      } else {
        // Redemption history
        const redemptionTypeLabel = (rt) =>
          rt === 'BILL_REDUCTION' ? 'Reduce in bill' : rt === 'GIFT' ? 'Gift' : rt === 'CASH' ? 'Cash' : rt || '–';
        exportData = (report.redemptionHistory || []).map((row, index) => ({
          'S.No': index + 1,
          'How redeemed': redemptionTypeLabel(row.redemptionType),
          'Bill # / Reference': row.reference || '–',
          'Date': row.redeemedAt ? format(new Date(row.redeemedAt), 'dd/MM/yyyy') : '–',
          'Points Redeemed': row.pointsRedeemed ?? 0,
          'Amount Adjusted (₹)': row.amountAdjusted || 0
        }));
        fileName = `loyalty_redemptions_${customerName.replace(/\s+/g, '_').toLowerCase()}`;
        sheetName = 'Points Redeemed';
        title = `Customer Loyalty - Redemption History (${customerName})`;
      }

      if (exportData.length === 0) {
        toast.error('No data to export in the current tab');
        setIsExporting(false);
        return;
      }

      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = tab === 0
        ? [{ wch: 8 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }]
        : [{ wch: 8 }, { wch: 18 }, { wch: 22 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
      ws['!cols'] = wscols;

      const wb = XLSX.utils.book_new();

      // Add report header
      XLSX.utils.sheet_add_aoa(ws, [
        [title],
        ['Generated on: ' + formatDateTime(new Date())],
        ['Customer: ' + customerName],
        ['Total Loyalty Points: ' + (report.totalLoyaltyPoints ?? 0)],
        ['']
      ], { origin: 'A1' });

      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting loyalty report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, py: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="xl">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">Dashboard</Link>
            <Link component={RouterLink} to="/reports" color="inherit" underline="hover">Reports</Link>
            <Typography color="text.primary">Customer Loyalty Report</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CardGiftcardIcon fontSize="large" color="primary" />
              <Typography variant="h4" fontWeight="bold">Customer Loyalty Report</Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportToExcel}
              disabled={isExporting || !report || (tab === 0 ? !report.purchaseHistory?.length : !report.redemptionHistory?.length)}
              startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View purchase history where the customer earned loyalty points (bills above your minimum purchase limit) and redemption details.
          </Typography>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Customer</InputLabel>
                    <Select
                      value={customerId}
                      label="Customer"
                      onChange={(e) => setCustomerId(e.target.value || '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      {customers.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name} {c.phone ? `(${c.phone})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {report && (
                  <Grid item xs={12} md={4}>
                    <Chip
                      icon={<CardGiftcardIcon />}
                      label={`Total points: ${report.totalLoyaltyPoints ?? 0}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : report ? (
            <>
              <Paper>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tab icon={<HistoryIcon />} iconPosition="start" label="Purchase history (points earned)" />
                  <Tab icon={<RedeemIcon />} iconPosition="start" label="Redemption details" />
                </Tabs>

                <TabPanel value={tab} index={0}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell><strong>Bill #</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell align="right"><strong>Total amount</strong></TableCell>
                          <TableCell align="right"><strong>Points earned</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!report.purchaseHistory || report.purchaseHistory.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No purchases with loyalty points yet. Bills above your minimum purchase limit will appear here.
                            </TableCell>
                          </TableRow>
                        ) : (
                          report.purchaseHistory.map((row) => (
                            <TableRow key={row.orderId} hover>
                              <TableCell>{row.orderNumber || '–'}</TableCell>
                              <TableCell>{row.orderDate ? format(new Date(row.orderDate), 'dd/MM/yyyy') : '–'}</TableCell>
                              <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                              <TableCell align="right">{row.pointsEarned ?? 0}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tab} index={1}>
                  {report.redemptionHistory && report.redemptionHistory.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell><strong>How redeemed</strong></TableCell>
                            <TableCell><strong>Bill # / Reference</strong></TableCell>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell align="right"><strong>Points redeemed</strong></TableCell>
                            <TableCell align="right"><strong>Amount adjusted (₹)</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.redemptionHistory.map((r) => {
                            const typeLabel =
                              r.redemptionType === 'BILL_REDUCTION'
                                ? 'Reduce in bill'
                                : r.redemptionType === 'GIFT'
                                  ? 'Gift'
                                  : r.redemptionType === 'CASH'
                                    ? 'Cash'
                                    : r.redemptionType || '–';
                            return (
                              <TableRow key={r.id}>
                                <TableCell>
                                  <Chip
                                    label={typeLabel}
                                    size="small"
                                    color={
                                      r.redemptionType === 'BILL_REDUCTION'
                                        ? 'primary'
                                        : r.redemptionType === 'GIFT'
                                          ? 'secondary'
                                          : r.redemptionType === 'CASH'
                                            ? 'success'
                                            : 'default'
                                    }
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{r.reference || '–'}</TableCell>
                                <TableCell>{r.redeemedAt ? format(new Date(r.redeemedAt), 'dd/MM/yyyy') : '–'}</TableCell>
                                <TableCell align="right">{r.pointsRedeemed ?? 0}</TableCell>
                                <TableCell align="right">{formatCurrency(r.amountAdjusted)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <RedeemIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography>No redemptions yet. Bill reduction, gift and cash redemptions will appear here.</Typography>
                    </Box>
                  )}
                </TabPanel>
              </Paper>
            </>
          ) : customerId ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>No data for this customer.</Typography>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>Select a customer to view loyalty report.</Typography>
          )}
        </Container>
      </Box>
    </Layout>
  );
};

export default CustomerLoyaltyReportPage;
